import { Popup, TagGroup } from "@hatch-digital-nl/abs-storybook/legacy";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
    resetFormValues,
    setEducatorInputValue,
    setFormValues,
    setIsEditCertificateModalOpen,
    setIsFunctionGroupModalOpen,
    setIsOpenFormLoading,
    setIsPatchEditCertificateSuccess,
    setIsQualificationStandardModalOpen,
    setSelectedCertificateId,
} from "./EditCertificateModalSlice";
import { JSX, useEffect, useRef, useState } from "react";
import { handleEditCertificate } from "./utils/handleEditCertificate";
import {
    useGetCertificateByIdQuery,
    usePatchCertificateMutation,
} from "../../common/api/certificate";
import { History } from "../../common/svg/History";
import { InfiniteImage } from "../../common/svg/InfiniteImage";
import { Person } from "../../common/svg/Person";
import { setFormFields } from "./utils/setFormFields";
import { calculateExpiresAt } from "./utils/calculateExpiresAt";
import { getValidityPeriodDisplay } from "../../common/utils/getValidityPeriodDisplay";
import { ValidityPeriodIconEnum } from "../../common/enums/ValidityPeriodIconEnum";
import { FileInputFieldComponent } from "./components/FileInputFieldComponent";
import { handleTagClickOutside } from "../../common/utils/handleTagClickOutside";
import { DateInputFieldComponent } from "./components/DateInputFieldComponent";
import { SubmitButtonComponent } from "./components/SubmitButtonComponent";
import { SelectFieldComponent } from "./components/SelectFieldComponent";
import { EducatorInterface } from "../../common/interfaces/EducatorInterface";
import { getUserFullName } from "../../common/utils/getUserFullName";
import { QualificationValidityTypeEnum } from "../../common/enums/QualificationValidityTypeEnum";

export const EditCertificateModalContainer = (): JSX.Element | null => {
    const dispatch = useAppDispatch();
    const functionGroupModalRef = useRef<HTMLDivElement>(null);
    const qualificationStandardModalRef = useRef<HTMLDivElement>(null);
    const [file, setFile] = useState<File | null>(null);

    const {
        selectedCertificateId,
        formValues,
        isEditModalOpen,
        error,
        approved,
        isFunctionGroupModalOpen,
        isQualificationStandardModalOpen,
        educatorInputValue,
    } = useAppSelector((state) => state.editCertificateModal);

    const [
        patchEditCertificate,
        {
            isLoading: isPatchEditCertificateLoading,
            isSuccess: isPatchEditCertificateSuccess,
        },
    ] = usePatchCertificateMutation();

    const {
        data: certificate,
        isLoading: certificateDataIsLoading,
        isFetching: certificateDataIsFetching,
    } = useGetCertificateByIdQuery(
        {
            id: selectedCertificateId,
        },
        {
            skip: !selectedCertificateId,
            refetchOnMountOrArgChange: true,
        },
    );

    useEffect(() => {
        dispatch(
            setIsOpenFormLoading(
                certificateDataIsLoading || certificateDataIsFetching,
            ),
        );
    }, [certificateDataIsLoading, certificateDataIsFetching, dispatch]);

    useEffect(() => {
        setFormFields({ dispatch, certificate });
    }, [certificate, isEditModalOpen, dispatch]);

    useEffect(() => {
        const validityPeriod = certificate?.qualification.validity_period;
        const hasPeriod =
            validityPeriod && (validityPeriod.months || validityPeriod.years);
        const hasFixedDate =
            certificate?.qualification.validity_type ===
                QualificationValidityTypeEnum.FIXED_DATE &&
            certificate?.qualification.fixed_valid_until;

        if (hasPeriod || hasFixedDate) {
            calculateExpiresAt({ dispatch, formValues });
        }
    }, [
        formValues?.validAt,
        formValues?.expiresAt,
        formValues?.qualification?.validityPeriod,
        formValues?.qualification?.validityType,
        formValues?.qualification?.fixedValidUntil,
        certificate?.qualification.validity_period,
        certificate?.qualification.validity_type,
        certificate?.qualification.fixed_valid_until,
        formValues,
        dispatch,
    ]);

    useEffect(() => {
        if (!isPatchEditCertificateSuccess) {
            dispatch(setIsPatchEditCertificateSuccess(false));
        }
    }, [isPatchEditCertificateSuccess, dispatch]);

    useEffect(() => {
        const clickOutsideHandler = (event: MouseEvent): void => {
            handleTagClickOutside({
                functionGroupModalRef,
                qualificationStandardModalRef,
                isFunctionGroupModalOpen,
                isQualificationStandardModalOpen,
                setIsFunctionGroupModalOpen,
                setIsQualificationStandardModalOpen,
                dispatch,
                event,
            });
        };

        document.addEventListener("mousedown", clickOutsideHandler);

        return () => {
            document.removeEventListener("mousedown", clickOutsideHandler);
        };
    }, [isFunctionGroupModalOpen, isQualificationStandardModalOpen, dispatch]);

    const validityPeriod = getValidityPeriodDisplay({
        validityPeriod: certificate?.qualification.validity_period,
        validityType: certificate?.qualification.validity_type,
        fixedValidUntil: certificate?.qualification.fixed_valid_until,
    });

    if (!certificate || certificateDataIsFetching || certificateDataIsLoading) {
        return null;
    }

    return (
        <Popup
            title={certificate?.qualification.name}
            className={"c-edit-certificate-modal"}
            onCloseClick={() => {
                dispatch(setIsEditCertificateModalOpen(false));
                dispatch(setSelectedCertificateId(undefined));
                dispatch(resetFormValues());
            }}
        >
            <div className={"c-edit-certificate-modal__header"}>
                {certificate.qualification.function_group.length > 0 && (
                    <TagGroup
                        tags={certificate.qualification.function_group}
                        tagColor={"#1D2D58"}
                        tagBackgroundColor={"#DAEBA8"}
                        isOpen={isFunctionGroupModalOpen}
                        handleClick={() =>
                            dispatch(
                                setIsFunctionGroupModalOpen(
                                    !isFunctionGroupModalOpen,
                                ),
                            )
                        }
                        modalRef={functionGroupModalRef}
                    />
                )}
                {certificate.qualification.qualification_standards.length >
                    0 && (
                    <TagGroup
                        tags={certificate.qualification.qualification_standards}
                        tagColor={"#FFFFFF"}
                        tagBackgroundColor={"#07A9D6"}
                        isOpen={isQualificationStandardModalOpen}
                        handleClick={() =>
                            dispatch(
                                setIsQualificationStandardModalOpen(
                                    !isQualificationStandardModalOpen,
                                ),
                            )
                        }
                        modalRef={qualificationStandardModalRef}
                    />
                )}
            </div>
            <div className={"c-edit-certificate-modal__validity-period"}>
                <div
                    className={
                        "c-edit-certificate-modal__validity-period__inner"
                    }
                >
                    {validityPeriod?.icon ===
                    ValidityPeriodIconEnum.INFINITE ? (
                        <InfiniteImage />
                    ) : (
                        <History />
                    )}
                    <p>{validityPeriod?.text}</p>
                </div>
            </div>
            <div className={"c-edit-certificate-modal__name"}>
                <Person />
                <p>{getUserFullName(certificate.employee.user)}</p>
            </div>
            {error && <div className={"c-error"}>{error}</div>}
            <form
                className={"c-edit-certificate-modal__form"}
                onSubmit={(event) => {
                    event.preventDefault();
                    handleEditCertificate({
                        certificate,
                        formValues,
                        dispatch,
                        patchEditCertificate,
                        file,
                        approved,
                    });
                }}
            >
                <SelectFieldComponent
                    id={1}
                    options={certificate.qualification.educators.map(
                        (educator: EducatorInterface) => ({
                            label: educator.name,
                            value: educator.id.toString(),
                        }),
                    )}
                    onChange={(newValue) => {
                        dispatch(
                            setFormValues({
                                ...formValues,
                                educator: {
                                    selectedId: newValue.value,
                                    selectedValue: newValue.label,
                                },
                            }),
                        );
                    }}
                    value={{
                        value: formValues?.educator?.selectedId || "",
                        label: formValues?.educator?.selectedValue || "",
                    }}
                    onInputChange={(newEducatorInputValue) => {
                        dispatch(setEducatorInputValue(newEducatorInputValue));
                    }}
                    inputValue={educatorInputValue}
                    errorMessage="Selecteer een opleider"
                    placeHolder="Zoek een opleider..."
                    title="Opleider"
                />
                <DateInputFieldComponent
                    formValues={formValues}
                    dispatch={dispatch}
                />
                <FileInputFieldComponent
                    formValues={formValues}
                    selectedCertificate={certificate}
                    dispatch={dispatch}
                    setFile={setFile}
                />
                <SubmitButtonComponent
                    isPatchEditCertificateLoading={
                        isPatchEditCertificateLoading
                    }
                />
            </form>
        </Popup>
    );
};
